import { useMemo, useState } from "react";
import useSWR from "swr";
import { getFetcher, deleteFetcher } from "../../../../../services/api";
import TableFilter from "../../../../elements/TableFilter";
import { month } from "../../../../../enums/month";
import ClientDataGrid from "../../../../elements/ClientDataGrid";
import ModalDetailPurchase from "./ModalDetailPurchase";
import toast from "react-hot-toast";
import { Button } from "@nextui-org/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

// Pilihan perusahaan & periode diteruskan induk supaya sama dengan tab Periode
// Aktif dan Preview; tanpa prop itu komponen tetap jalan dengan state sendiri.
export default function TableHistoryPurchase({ dataCompany, isLoadingCompany, selectedCompany, setSelectedCompany, selectedPeriod, setSelectedPeriod }) {
    const [companySendiri, setCompanySendiri] = useState(null);
    const [periodeSendiri, setPeriodeSendiri] = useState(null);

    const dikendalikanInduk = typeof setSelectedPeriod === 'function';
    const company = dikendalikanInduk ? selectedCompany : companySendiri;
    const setCompany = typeof setSelectedCompany === 'function' ? setSelectedCompany : setCompanySendiri;
    const period = dikendalikanInduk ? selectedPeriod : periodeSendiri;
    const setPeriod = dikendalikanInduk ? setSelectedPeriod : setPeriodeSendiri;
    const { data: dataPeriod, isLoading: isLoadingPeriod } = useSWR(`/api/period/all`, getFetcher);
    const { data, isLoading, mutate } = useSWR(
        company && period
            ? `/api/pelaporan-pembelian/search?returnAll=true&companyId=${company.value}&periodId=${period.value}`
            : null, 
        getFetcher
    );

    // State untuk mengontrol Modal Delete
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

    // Fungsi untuk membuka modal konfirmasi
    const handleDeleteClick = (id) => {
        setDeleteModal({ isOpen: true, id: id });
    };

    // Fungsi eksekusi hapus data
    const executeDelete = async () => {
        // Opsional: Anda bisa menambahkan loading toast di sini jika proses hapusnya agak lama
        // const toastId = toast.loading("Menghapus data...");

        try {
            await deleteFetcher('/api/pelaporan-pembelian/delete', deleteModal.id);

            // 2. Ganti alert sukses dengan toast.success
            toast.success("Data berhasil dihapus!"); 
            // Jika pakai loading toast: toast.success("Data berhasil dihapus!", { id: toastId });

            mutate(); // Refresh tabel otomatis via SWR
            
        } catch (error) {
            console.error("Terjadi kesalahan:", error);
            const errorMsg = error?.response?.data?.message || "Gagal menghapus data. Silakan coba lagi.";
            
            // 3. Ganti alert error dengan toast.error
            toast.error(errorMsg);
            // Jika pakai loading toast: toast.error(errorMsg, { id: toastId });

        } finally {
            setDeleteModal({ isOpen: false, id: null });
        }
    };

    const columns = useMemo(() =>  [
        { field: 'perusahaan', headerName: 'Perusahaan', valueGetter: (value, row) => row?.company?.name },
        { field: 'jenisb3', headerName: 'Jenis B3', valueGetter: (value, row) => row?.dataBahanB3?.namaBahanKimia },
        { field: 'jumlahPembelianB3', headerName: 'Jumlah Pembelian', valueGetter: (value, row) => `${row.jumlahPembelianB3} Kg` },
        { field: 'periode', headerName: 'Periode', valueGetter: (value, row) => row.period.name },
        { field: 'bulan', headerName: 'Bulan', valueGetter: (value, row) => month[row.bulan-1] },
        { field: 'tahun', headerName: 'Tahun' },
        { field: 'tipePembelian', headerName: 'Tipe Pembelian', },
        { field: 'status', headerName: 'Status', valueGetter: (value, row) => row.status ?? '-' }, 
        { 
            field: 'supplier', 
            headerName: 'Supplier',
            valueGetter: (value, row) => `${row?.DataSupplierOnPelaporanPembelianBahanB3?.map((item) => `${item.dataSupplier.namaSupplier}: ${item.jumlahBahanB3Dibeli} Kg`).join(', ')}` || '-'
        },
        { 
            field: 'import', 
            headerName: 'Data Impor',
            valueGetter: (value, row) => `${row?.DataImportOnPelaporanPembelianBahanB3?.map((item) => `${item.pelabuhanBongkar.nm_kantor_pendek}: ${item.jumlahBahanB3Dibeli} Kg`).join(', ')}` || '-'
        },
        {
            field: 'action',
            headerName: 'Aksi',
            width: 100, 
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <ModalDetailPurchase item={params.row} />

                    {params.row.status === null && (
                        <Button 
                            isIconOnly 
                            size="sm" 
                            color="danger" 
                            onPress={() => handleDeleteClick(params.row.id)}
                            title="Hapus Data"
                        >
                            <XMarkIcon className="size-4" />
                        </Button>
                    )}
                </div>
            ),
            sortable: false,
            filterable: false
        },
    ], []); 
    
    return(
        <div className="h-[550px]">
            <TableFilter
                company={company}
                period={period}
                setCompany={setCompany}
                setPeriod={setPeriod}
                dataCompany={dataCompany}
                isLoadingCompany={isLoadingCompany}
                dataPeriod={dataPeriod}
                isLoadingPeriod={isLoadingPeriod}
            />
            <ClientDataGrid
                columns={columns}
                rows={data?.data}
                isLoading={isLoading}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            supplier: false,
                            import: false,
                        },
                    },
                }}
            />
            
            {/* --- Modal Konfirmasi Delete --- */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-5">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-center text-ink-900">Konfirmasi Hapus</h3>
                            <p className="mt-2 text-sm text-center text-ink-500">
                                Apakah Anda yakin ingin menghapus data ini? Data yang sudah dihapus tidak dapat dikembalikan.
                            </p>
                        </div>
                        <div className="flex px-4 py-3 bg-surface-2 gap-3 justify-end">
                            <button
                                onClick={() => setDeleteModal({ isOpen: false, id: null })}
                                className="px-4 py-2 text-sm font-medium text-ink-700 bg-white border border-line-strong rounded-md shadow-sm hover:bg-surface-2 focus:outline-none"
                            >
                                Batal
                            </button>
                            <button
                                onClick={executeDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none"
                            >
                                Ya, Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

